/**
 * ChatPanel — the conversation half of /coach.
 *
 * Scrollable transcript on top, composer on the bottom. Before the
 * first user message the four Quick-Start chips sit above the input
 * to give new users an immediate way in. A typing indicator swaps in
 * while the mock endpoint is in flight.
 *
 * The panel is purely presentational: all state changes (sending,
 * committing replies, applying draft patches) live on CoachPage so
 * that the handoff wiring stays in one place.
 */

import { useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { cn } from '@/utils/cn';

import { MessageBubble } from './MessageBubble';
import { QuickStartChips } from './QuickStartChips';
import { TypingIndicator } from './TypingIndicator';
import { shouldShowQuickStart } from './store';
import type { CoachMessage } from './store';
import type { CoachDomain } from './trees';
import type { CoachState } from './engine';

export function ChatPanel({
  messages,
  engine,
  thinking,
  onSend,
  onPickDomain,
  disabled,
}: {
  messages: CoachMessage[];
  engine: CoachState;
  thinking: boolean;
  onSend: (text: string) => void;
  onPickDomain: (id: CoachDomain) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('coach');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const showChips = shouldShowQuickStart(engine);

  // Autoscroll on new message or when the typing indicator appears so
  // the latest turn is always in view.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, thinking]);

  // Pull focus back to the input after the Coach finishes a reply so
  // the user can keep typing without hunting for the cursor.
  useEffect(() => {
    if (!thinking && !disabled) {
      inputRef.current?.focus();
    }
  }, [thinking, disabled]);

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!inputRef.current) return;
    const text = inputRef.current.value.trim();
    if (!text || thinking || disabled) return;
    onSend(text);
    inputRef.current.value = '';
    inputRef.current.style.height = 'auto';
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  }

  function autoGrow(e: FormEvent<HTMLTextAreaElement>) {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  return (
    <div className="flex h-full min-h-[480px] flex-col rounded-xl border border-border bg-surface">
      {/* Transcript */}
      <ul
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={t('chat.transcript')}
        className="flex-1 space-y-3 overflow-y-auto p-4"
      >
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {thinking ? (
          <li className="flex justify-start ps-9">
            <TypingIndicator />
          </li>
        ) : null}
        <div ref={listEndRef} aria-hidden="true" />
      </ul>

      {/* Composer */}
      <form
        onSubmit={submit}
        className="flex flex-col gap-2 border-t border-border p-3"
      >
        {showChips ? (
          <div className="pb-1">
            <p className="mb-2 text-[11px] uppercase tracking-wider text-ink-subtle">
              {t('chat.quickstart_label')}
            </p>
            <QuickStartChips onPick={onPickDomain} onSendText={onSend} />
          </div>
        ) : null}

        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={t('chat.placeholder')}
            aria-label={t('chat.placeholder')}
            onKeyDown={onKeyDown}
            onInput={autoGrow}
            disabled={disabled || thinking}
            className={cn(
              'flex-1 resize-none rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink',
              'placeholder:text-ink-subtle focus-visible:border-spark focus-visible:outline-none',
              'disabled:opacity-60',
            )}
          />
          <Button type="submit" variant="primary" size="md" disabled={disabled || thinking}>
            {t('chat.send')}
          </Button>
        </div>
      </form>
    </div>
  );
}
